// Lab Workflow module DTOs — the "WorkFlow 2026" master-case + configurable-stage model.
// One case = one master record (`WfCase`) that moves between configurable stages; every movement
// and every action is an immutable `WfEvent`. Queues are filters on `stageKey`, never copies.
// Imported by both server (validation, repo mapping) and client (typing, forms).
import { z } from 'zod';

// ---------------------------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------------------------

export const WF_STAGE_GROUPS = [
  'intake',
  'records',
  'design',
  'review',
  'approval',
  'billing',
  'production',
  'finishing',
  'dispatch',
  'terminal',
  'special',
] as const;
export type WfStageGroup = (typeof WF_STAGE_GROUPS)[number];

export interface WfStage {
  id: string;
  key: string;
  name: string;
  sortOrder: number;
  group: WfStageGroup;
  color: string | null;
  isTerminal: boolean;
  isActive: boolean;
  /** SLA in hours before a case in this stage is flagged as stuck (null = no SLA). */
  slaHours: number | null;
  /** Case fields that must be non-empty before a case may ENTER this stage. */
  requiredFields: string[];
  /** Checklist template (labels) that must be completed before LEAVING this stage. */
  checklist: string[];
  /** Stage keys a case may move to from here (empty = any active stage). */
  allowedNext: string[];
  /** Whether cards in this stage appear on the Kanban board. */
  onBoard: boolean;
}

export const WF_CASE_FIELD_KEYS = [
  'patientFirstName',
  'patientLastName',
  'phone',
  'whatsapp',
  'email',
  'doctorName',
  'clinicName',
  'groupId',
  'receivedThrough',
  'caseTypes',
  'dueDate',
  'assignedTo',
  'reviewerId',
  'productionInstructions',
  'deliveryAddress',
  'iprStatus',
  'etchingStatus',
] as const;

export const wfStageInputSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1)
    .max(48)
    .regex(/^[a-z0-9_]+$/, 'lowercase letters, digits, underscore'),
  name: z.string().trim().min(1).max(80),
  sortOrder: z.number().int().min(0).max(10000),
  group: z.enum(WF_STAGE_GROUPS),
  color: z.string().trim().max(24).nullish(),
  isTerminal: z.boolean().optional(),
  isActive: z.boolean().optional(),
  slaHours: z.number().min(0).max(100000).nullish(),
  requiredFields: z.array(z.string().max(48)).max(50).optional(),
  checklist: z.array(z.string().trim().min(1).max(160)).max(50).optional(),
  allowedNext: z.array(z.string().max(48)).max(100).optional(),
  onBoard: z.boolean().optional(),
});
export type WfStageInput = z.infer<typeof wfStageInputSchema>;

export const wfStagesReplaceSchema = z.object({ stages: z.array(wfStageInputSchema).min(1).max(200) });

export const WF_OPTION_KINDS = [
  'case_type',
  'received_through',
  'hold_reason',
  'cancel_reason',
  'priority',
  'approval_method',
  'courier',
  'packaging_checklist',
  'touchup_checklist',
  'punching_type',
  'notification_rule',
  'file_category',
  'note_kind',
] as const;
export type WfOptionKind = (typeof WF_OPTION_KINDS)[number];

export interface WfOption {
  id: string;
  kind: WfOptionKind;
  key: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
  meta: Record<string, unknown> | null;
}

export const wfOptionInputSchema = z.object({
  kind: z.enum(WF_OPTION_KINDS),
  key: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_.-]+$/, 'lowercase key'),
  label: z.string().trim().min(1).max(120),
  sortOrder: z.number().int().min(0).max(10000).optional(),
  isActive: z.boolean().optional(),
  meta: z.record(z.unknown()).nullish(),
});
export type WfOptionInput = z.infer<typeof wfOptionInputSchema>;
export const wfOptionPatchSchema = wfOptionInputSchema.partial().omit({ kind: true, key: true });

export interface WfDoctor {
  id: string;
  code: string; // = Group ID in the workbook
  name: string;
  clinicName: string | null;
  branch: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  address: string | null;
  notes: string | null;
  /** The clinic account this group belongs to. This link — not a code match — decides which cases a
   *  clinic sees in its portal. */
  clinicId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const optText = (max: number) => z.string().trim().max(max).nullish();

export const wfDoctorInputSchema = z.object({
  code: z.string().trim().min(1).max(40),
  name: z.string().trim().min(1).max(120),
  clinicName: optText(160),
  branch: optText(120),
  phone: optText(40),
  whatsapp: optText(40),
  email: optText(160),
  address: optText(400),
  notes: optText(2000),
  clinicId: z.string().nullish(),
  isActive: z.boolean().optional(),
});
export type WfDoctorInput = z.infer<typeof wfDoctorInputSchema>;
export const wfDoctorPatchSchema = wfDoctorInputSchema.partial();

// ---------------------------------------------------------------------------------------------
// Master case
// ---------------------------------------------------------------------------------------------

export const WF_PRIORITIES = ['normal', 'urgent', 'emergency'] as const;
export type WfPriority = (typeof WF_PRIORITIES)[number];

export const WF_REQ_STATUSES = ['not_required', 'required', 'pending', 'completed'] as const;
export type WfReqStatus = (typeof WF_REQ_STATUSES)[number];

export const WF_APPROVAL_STATES = [
  'none',
  'simulation_ready',
  'requested',
  'pending',
  'approved',
  'rejected',
  'changes_requested',
  'approved_with_instructions',
] as const;
export type WfApprovalState = (typeof WF_APPROVAL_STATES)[number];

export interface WfCase {
  id: string;
  caseNumber: string;
  groupId: string | null;
  doctorId: string | null;
  doctorName: string | null;
  clinicName: string | null;
  branch: string | null;
  patientId: string | null;
  patientFirstName: string;
  patientLastName: string | null;
  patientName: string;
  patientCode: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  patientInfo: string | null;
  receivedThrough: string | null;
  receivedThroughRaw: string | null;
  caseType: string; // primary
  caseTypes: string[]; // all applicable products
  caseTypeRaw: string | null;
  trays: number | null; // number of clear-aligner trays (drives clinic pricing)
  arch: 'upper' | 'lower' | 'both' | null; // which models the case is for
  upperTrays: number | null; // aligners for the upper arch (trays = upper + lower when both are set)
  lowerTrays: number | null;
  // Production counters per arch: how many of the upper/lower trays have been printed, punched and
  // cut. Remaining = upperTrays/lowerTrays minus these.
  printedUpper: number;
  printedLower: number;
  punchedUpper: number;
  punchedLower: number;
  cutUpper: number;
  cutLower: number;
  discountType: 'percent' | 'amount' | null; // discount on the aligner price (see aligner-pricing)
  discountValue: number | null; // percent (0-100) or whole PKR, per discountType
  currentTray: number | null; // which tray the patient is on (patient app)
  trayStartedAt: string | null; // ISO date the current tray began
  stageKey: string;
  stageEnteredAt: string;
  previousStageKey: string | null;
  priority: WfPriority;
  dueDate: string | null; // ISO date
  receivedAt: string;
  assignedTo: string | null;
  assignedToName: string | null;
  reviewerId: string | null;
  reviewerName: string | null;
  notes: string | null;
  additionalNotes: string | null;
  internalInstructions: string | null;
  patientInstructions: string | null;
  productionInstructions: string | null;
  accountsInstructions: string | null;
  deliveryAddress: string | null;
  deliveryInstructions: string | null;
  iprStatus: WfReqStatus;
  iprNotes: string | null;
  etchingStatus: WfReqStatus;
  etchingNotes: string | null;
  punching: string | null;
  grade: string | null;
  newOrRefinement: 'new' | 'refinement' | null;
  approvalState: WfApprovalState;
  productionApproved: boolean;
  paymentStatus: string | null;
  setupVersion: number;
  printVersion: number;
  reprintCount: number;
  reworkCount: number;
  holdReason: string | null;
  holdSince: string | null;
  cancelReason: string | null;
  cancelledAt: string | null;
  dispatchedAt: string | null;
  trackingNumber: string | null;
  courier: string | null;
  checklistState: Record<string, boolean>; // current stage checklist progress
  tags: string[];
  source: 'manual' | 'import';
  importRef: string | null;
  createdBy: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // derived (server computes at read time)
  daysInStage: number;
  hoursInStage: number;
  isOverdue: boolean;
  dueInDays: number | null;
  slaBreached: boolean;
}

const optDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')
  .nullish();

export const wfCaseCreateSchema = z.object({
  caseNumber: z.string().trim().max(40).nullish(),
  groupId: z.string().trim().max(40).nullish(),
  doctorId: z.string().nullish(),
  /** The lab clinic record the intake form was filled against. Binds the doctor group to that
   *  clinic account so the case shows up in its portal. */
  clinicId: z.string().nullish(),
  doctorName: optText(120),
  clinicName: optText(160),
  branch: optText(120),
  patientId: z.string().nullish(),
  patientFirstName: z.string().trim().min(1).max(120),
  patientLastName: optText(120),
  patientCode: optText(40),
  phone: optText(40),
  whatsapp: optText(40),
  email: optText(160),
  patientInfo: optText(2000),
  receivedThrough: optText(64),
  caseTypes: z.array(z.string().trim().min(1).max(64)).min(1).max(10),
  trays: z.number().int().min(0).max(500).nullish(),
  arch: z.enum(['upper', 'lower', 'both']).nullish(),
  upperTrays: z.number().int().min(0).max(500).nullish(),
  lowerTrays: z.number().int().min(0).max(500).nullish(),
  // A discount on the aligner price. Percent is capped at 100 when applied; an amount is capped at
  // the aligner charge itself, so neither can push an invoice negative.
  discountType: z.enum(['percent', 'amount']).nullish(),
  discountValue: z.number().min(0).max(10_000_000).nullish(),
  stageKey: z.string().trim().max(48).nullish(),
  priority: z.enum(WF_PRIORITIES).optional(),
  dueDate: optDate,
  receivedAt: z.string().nullish(),
  assignedTo: z.string().nullish(),
  reviewerId: z.string().nullish(),
  notes: optText(4000),
  additionalNotes: optText(4000),
  internalInstructions: optText(4000),
  patientInstructions: optText(4000),
  productionInstructions: optText(4000),
  accountsInstructions: optText(4000),
  deliveryAddress: optText(600),
  deliveryInstructions: optText(2000),
  iprStatus: z.enum(WF_REQ_STATUSES).optional(),
  iprNotes: optText(2000),
  etchingStatus: z.enum(WF_REQ_STATUSES).optional(),
  etchingNotes: optText(2000),
  punching: optText(40),
  grade: optText(40),
  newOrRefinement: z.enum(['new', 'refinement']).nullish(),
  paymentStatus: optText(40),
  tags: z.array(z.string().trim().max(40)).max(20).optional(),
  /** Admin-only escape hatch for a detected duplicate (same group + same patient). */
  allowDuplicate: z.boolean().optional(),
});
export type WfCaseCreate = z.infer<typeof wfCaseCreateSchema>;

export const wfCasePatchSchema = wfCaseCreateSchema
  .omit({ allowDuplicate: true, stageKey: true, caseNumber: true })
  .partial()
  .extend({
    caseNumber: z.string().trim().min(1).max(40).optional(),
    productionApproved: z.boolean().optional(),
    currentTray: z.number().int().min(1).max(500).nullish(),
    trayStartedAt: z.string().nullish(),
    checklistState: z.record(z.boolean()).optional(),
    printedUpper: z.number().int().min(0).max(500).optional(),
    printedLower: z.number().int().min(0).max(500).optional(),
    punchedUpper: z.number().int().min(0).max(500).optional(),
    punchedLower: z.number().int().min(0).max(500).optional(),
    cutUpper: z.number().int().min(0).max(500).optional(),
    cutLower: z.number().int().min(0).max(500).optional(),
  });
export type WfCasePatch = z.infer<typeof wfCasePatchSchema>;

export interface WfCaseListQuery {
  search?: string;
  stage?: string[]; // stage keys
  group?: string; // stage group
  caseType?: string[];
  doctor?: string; // doctorId or groupId text
  clinic?: string;
  assignedTo?: string;
  reviewerId?: string;
  priority?: WfPriority[];
  receivedThrough?: string[];
  ipr?: WfReqStatus[];
  etching?: WfReqStatus[];
  approval?: WfApprovalState[];
  overdue?: boolean;
  due?: 'today' | 'tomorrow' | 'week' | 'overdue';
  dueFrom?: string;
  dueTo?: string;
  receivedFrom?: string;
  receivedTo?: string;
  includeTerminal?: boolean;
  includeDeleted?: boolean;
  stuck?: boolean; // SLA breached
  patientId?: string;
  sort?: 'received' | 'due' | 'updated' | 'stage_age' | 'patient';
  dir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface WfCaseListResponse {
  items: WfCase[];
  total: number;
  limit: number;
  offset: number;
}

export interface WfBoardColumn {
  stage: WfStage;
  count: number;
  cases: WfCase[];
}
export interface WfBoardResponse {
  columns: WfBoardColumn[];
  generatedAt: string;
}

// ---------------------------------------------------------------------------------------------
// Timeline / history
// ---------------------------------------------------------------------------------------------

export const WF_EVENT_TYPES = [
  'created',
  'imported',
  'stage_change',
  'field_change',
  'assigned',
  'note',
  'file',
  'approval',
  'review',
  'hold',
  'resume',
  'cancel',
  'reopen',
  'setup_version',
  'production',
  'dispatch',
  'rework',
  'reprint',
  'checklist',
  'bulk',
] as const;
export type WfEventType = (typeof WF_EVENT_TYPES)[number];

export interface WfEvent {
  id: string;
  caseId: string;
  at: string;
  actorUserId: string | null;
  actorName: string | null;
  eventType: WfEventType;
  fromStage: string | null;
  toStage: string | null;
  comment: string | null;
  data: Record<string, unknown> | null;
}

export interface WfStageVisit {
  id: string;
  caseId: string;
  stageKey: string;
  enteredAt: string;
  exitedAt: string | null;
  seconds: number | null;
}

// ---------------------------------------------------------------------------------------------
// Notes / files
// ---------------------------------------------------------------------------------------------

export const WF_NOTE_KINDS = ['internal', 'production', 'doctor', 'patient', 'delivery', 'additional'] as const;
export type WfNoteKind = (typeof WF_NOTE_KINDS)[number];

export interface WfNote {
  id: string;
  caseId: string;
  kind: WfNoteKind;
  body: string;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
  pinned: boolean;
}
export const wfNoteInputSchema = z.object({
  kind: z.enum(WF_NOTE_KINDS).default('internal'),
  body: z.string().trim().min(1).max(8000),
  pinned: z.boolean().optional(),
});

export const WF_FILE_CATEGORIES = [
  'stl',
  'scan',
  'image',
  'xray',
  'pdf',
  'video',
  'treatment_plan',
  'setup',
  'screenshot',
  'doctor_instruction',
  'approval_evidence',
  /** Patient-submitted progress photo. Surfaced in the compliance view, not the records list. */
  'progress_photo',
  'other',
] as const;
export type WfFileCategory = (typeof WF_FILE_CATEGORIES)[number];

export interface WfFile {
  id: string;
  caseId: string;
  fileName: string;
  originalName: string;
  mimeType: string | null;
  sizeBytes: number;
  category: WfFileCategory;
  version: number;
  replacesFileId: string | null;
  description: string | null;
  uploadedBy: string | null;
  uploadedByName: string | null;
  uploadedAt: string;
  /** Set when the file has been removed from the case. The bytes stay; nothing lists or serves it. */
  deletedAt: string | null;
  /** The lab has put this artifact in front of the doctor to approve (usually a model video). */
  sharedForApproval: boolean;
  sharedAt: string | null;
  /** Message shown to the doctor above the player. */
  shareNote: string | null;
}

// ---------------------------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------------------------

export const wfDispatchSchema = z.object({
  dispatchedAt: z.string().nullish(),
  courier: optText(80),
  trackingNumber: optText(120),
  address: optText(600),
  receiver: optText(160),
  notes: optText(2000),
  contents: optText(2000),
});

export const wfMoveSchema = z.object({
  toStage: z.string().trim().min(1).max(48),
  comment: optText(2000),
  /** Optional field patch applied atomically before validation (e.g. fill required fields). */
  patch: wfCasePatchSchema.optional(),
  /** Mark current stage checklist complete as part of the move. */
  checklistState: z.record(z.boolean()).optional(),
  /**
   * Dispatch details recorded as part of a move INTO Dispatched, so the Move dialog can collect the
   * courier / receiver / address it would otherwise reject the move for lacking. Ignored for any
   * other target stage. Needs workflow:dispatch on top of workflow:move.
   */
  dispatch: wfDispatchSchema.optional(),
});
export type WfMoveInput = z.infer<typeof wfMoveSchema>;

export const wfAssignSchema = z.object({
  assignedTo: z.string().nullable().optional(),
  reviewerId: z.string().nullable().optional(),
  comment: optText(1000),
});

export const wfHoldSchema = z.object({
  reason: z.string().trim().min(1).max(64),
  responsibleUserId: z.string().nullish(),
  expectedAt: optDate,
  notes: optText(4000),
});
export const wfResumeSchema = z.object({
  toStage: z.string().trim().max(48).nullish(), // default: previous stage
  comment: optText(2000),
});
export const wfCancelSchema = z.object({
  reason: z.string().trim().min(1).max(64),
  notes: optText(4000),
});
export const wfReopenSchema = z.object({
  toStage: z.string().trim().max(48).nullish(),
  comment: optText(2000),
});

/**
 * Approvals the lab can ask for.
 *
 * There is no patient approval: the treating doctor is the lab's customer and the only person who
 * signs off a case. The patient never sees a setup to approve — they get aligners, a wear tracker
 * and progress photos, which is a different relationship entirely.
 */
export const WF_APPROVAL_KINDS = ['doctor', 'production'] as const;

/**
 * Every kind that can appear in the record, including 'patient' from before that was removed.
 * History is never rewritten — a case held for a patient approval in 2025 must still say so.
 */
export const WF_APPROVAL_KINDS_ALL = ['doctor', 'production', 'patient'] as const;
export type WfApprovalKind = (typeof WF_APPROVAL_KINDS_ALL)[number];

export const WF_APPROVAL_KIND_LABELS: Record<WfApprovalKind, string> = {
  doctor: 'Doctor approval',
  production: 'Production approval',
  patient: 'Patient approval (retired)',
};

export interface WfApproval {
  id: string;
  caseId: string;
  kind: WfApprovalKind;
  status: WfApprovalState;
  method: string | null;
  comments: string | null;
  reason: string | null;
  requestedBy: string | null;
  requestedByName: string | null;
  requestedAt: string;
  decidedBy: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  evidenceFileId: string | null;
  returnToStage: string | null;
}

export const wfApprovalRequestSchema = z.object({
  kind: z.enum(WF_APPROVAL_KINDS).default('doctor'),
  method: optText(40),
  comments: optText(2000),
  simulationReady: z.boolean().optional(),
});
export const wfApprovalDecideSchema = z.object({
  kind: z.enum(WF_APPROVAL_KINDS).default('doctor'),
  decision: z.enum(['approved', 'rejected', 'changes_requested', 'approved_with_instructions']),
  method: optText(40),
  comments: optText(4000),
  reason: optText(2000),
  evidenceFileId: z.string().nullish(),
  /** For rejected / changes_requested: where the case goes back to. */
  returnToStage: z.string().trim().max(48).nullish(),
  /** For approved: optionally move forward (default: 'approved'). */
  nextStage: z.string().trim().max(48).nullish(),
});

export const WF_REVIEW_QUEUES = ['mubeen_awais', 'dr_ali'] as const;
export type WfReviewQueue = (typeof WF_REVIEW_QUEUES)[number];
export const WF_REVIEW_DECISIONS = ['approved', 'rejected', 'changes_requested', 'on_hold', 'commented'] as const;

export interface WfReview {
  id: string;
  caseId: string;
  queue: WfReviewQueue;
  decision: (typeof WF_REVIEW_DECISIONS)[number];
  reviewerId: string | null;
  reviewerName: string | null;
  at: string;
  comment: string | null;
  instructions: string | null;
  nextStage: string | null;
}
export const wfReviewSchema = z.object({
  queue: z.enum(WF_REVIEW_QUEUES),
  decision: z.enum(WF_REVIEW_DECISIONS),
  comment: optText(4000),
  instructions: optText(4000),
  nextStage: z.string().trim().max(48).nullish(),
  holdReason: optText(64),
});

export interface WfHold {
  id: string;
  caseId: string;
  reason: string;
  responsibleUserId: string | null;
  responsibleName: string | null;
  placedAt: string;
  placedBy: string | null;
  placedByName: string | null;
  expectedAt: string | null;
  notes: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  resumeStage: string | null;
  fromStage: string | null;
}

export const WF_SETUP_KINDS = ['initial_setup', 'setup_3d', 'simulation', 'video', 'presentation', 'revision'] as const;
export interface WfSetupVersion {
  id: string;
  caseId: string;
  version: number;
  kind: (typeof WF_SETUP_KINDS)[number];
  status: 'draft' | 'shared' | 'doctor_review' | 'approved' | 'rejected' | 'superseded';
  notes: string | null;
  fileId: string | null;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
}
export const wfSetupVersionSchema = z.object({
  kind: z.enum(WF_SETUP_KINDS).default('setup_3d'),
  status: z.enum(['draft', 'shared', 'doctor_review', 'approved', 'rejected', 'superseded']).optional(),
  notes: optText(4000),
  fileId: z.string().nullish(),
});

export const WF_PRODUCTION_KINDS = [
  'print_order',
  'print_run',
  'print_qc',
  'punching',
  'touch_up',
  'finishing',
  'packaging',
  'reprint',
  'rework',
  'qc',
] as const;
export type WfProductionKind = (typeof WF_PRODUCTION_KINDS)[number];

export interface WfProductionRecord {
  id: string;
  caseId: string;
  kind: WfProductionKind;
  status: 'open' | 'in_progress' | 'completed' | 'failed';
  version: number;
  data: Record<string, unknown>;
  checklist: Array<{ label: string; done: boolean }>;
  staffId: string | null;
  staffName: string | null;
  startedAt: string | null;
  completedAt: string | null;
  notes: string | null;
  createdAt: string;
}
export const wfProductionSchema = z.object({
  kind: z.enum(WF_PRODUCTION_KINDS),
  status: z.enum(['open', 'in_progress', 'completed', 'failed']).optional(),
  data: z.record(z.unknown()).optional(),
  checklist: z.array(z.object({ label: z.string().max(200), done: z.boolean() })).max(60).optional(),
  staffId: z.string().nullish(),
  startedAt: z.string().nullish(),
  completedAt: z.string().nullish(),
  notes: optText(4000),
  /** Optional stage to move to on save (validated like /move). */
  moveTo: z.string().trim().max(48).nullish(),
});
export const wfProductionPatchSchema = wfProductionSchema.partial().omit({ kind: true });

export const wfReworkSchema = z.object({
  kind: z.enum(['rework', 'reprint']),
  reason: z.string().trim().min(1).max(2000),
  instructions: optText(4000),
  /** Where the case goes back to (default: printing_in_progress for reprint, in_finishing for rework). */
  toStage: z.string().trim().max(48).nullish(),
});

export interface WfDispatch {
  id: string;
  caseId: string;
  dispatchedAt: string;
  courier: string | null;
  trackingNumber: string | null;
  address: string | null;
  receiver: string | null;
  staffId: string | null;
  staffName: string | null;
  notes: string | null;
  contents: string | null;
  updatedAt: string;
}
export const wfBulkSchema = z.object({
  ids: z.array(z.string()).min(1).max(500),
  action: z.enum(['assign', 'priority', 'move', 'note', 'reviewer']),
  assignedTo: z.string().nullish(),
  reviewerId: z.string().nullish(),
  priority: z.enum(WF_PRIORITIES).optional(),
  toStage: z.string().trim().max(48).optional(),
  note: optText(4000),
  noteKind: z.enum(WF_NOTE_KINDS).optional(),
  comment: optText(2000),
  confirm: z.literal(true),
});

// ---------------------------------------------------------------------------------------------
// Workspace (case detail payload)
// ---------------------------------------------------------------------------------------------

export interface WfCaseWorkspace {
  case: WfCase;
  stage: WfStage | null;
  events: WfEvent[];
  stageVisits: WfStageVisit[];
  notes: WfNote[];
  files: WfFile[];
  approvals: WfApproval[];
  reviews: WfReview[];
  holds: WfHold[];
  setupVersions: WfSetupVersion[];
  production: WfProductionRecord[];
  dispatch: WfDispatch | null;
  patientCases: Array<Pick<WfCase, 'id' | 'caseNumber' | 'caseType' | 'stageKey' | 'receivedAt' | 'dueDate'>>;
  /** Stage keys this case may move to right now (given config + terminal rules). */
  allowedNext: string[];
  /** The case's live invoice: what the clinic is actually charged, including hand edits. */
  invoice: { invoiceNumber: string; totalPaisa: number } | null;
}

// ---------------------------------------------------------------------------------------------
// Clinic portal — case submission
// ---------------------------------------------------------------------------------------------

/**
 * Named record slots offered to the clinic at submission, grouped. Each slot is a labelled tile the
 * clinic fills, so missing records are visible at a glance rather than hidden in a generic dropzone.
 * `category` maps the slot onto a WfFileCategory for storage.
 */
export interface WfRecordSlot {
  key: string;
  label: string;
  category: WfFileCategory;
}
export interface WfRecordSlotGroup {
  key: string;
  label: string;
  hint: string;
  slots: WfRecordSlot[];
}

export const WF_RECORD_SLOT_GROUPS: WfRecordSlotGroup[] = [
  {
    key: 'scans',
    label: 'Digital scans',
    hint: 'STL / PLY / OBJ intraoral scan files',
    slots: [
      { key: 'upper_arch_stl', label: 'Upper Arch STL', category: 'stl' },
      { key: 'lower_arch_stl', label: 'Lower Arch STL', category: 'stl' },
      { key: 'bite_scan_stl', label: 'Bite Scan STL', category: 'stl' },
      { key: 'second_bite_scan', label: '2nd Bite Scan', category: 'stl' },
      { key: 'full_arch_stl', label: 'Full Arch STL', category: 'stl' },
      { key: 'pre_op_scan', label: 'Pre-op Scan', category: 'scan' },
      { key: 'post_op_scan', label: 'Post-op Scan', category: 'scan' },
    ],
  },
  {
    key: 'radiographs',
    label: 'Radiographs',
    hint: 'X-rays and CBCT',
    slots: [
      { key: 'opg_pano', label: 'OPG / Pano', category: 'xray' },
      { key: 'lateral_ceph', label: 'Lateral Ceph', category: 'xray' },
      { key: 'pa_ceph', label: 'PA Ceph', category: 'xray' },
      { key: 'bitewing', label: 'Bitewing', category: 'xray' },
      { key: 'periapical', label: 'Periapical', category: 'xray' },
      { key: 'cbct_dicom', label: 'CBCT / DICOM', category: 'scan' },
    ],
  },
  {
    key: 'extraoral',
    label: 'Extraoral photos',
    hint: 'Facial photographs',
    slots: [
      { key: 'frontal_rest', label: 'Frontal · Rest', category: 'image' },
      { key: 'frontal_smile', label: 'Frontal · Smile', category: 'image' },
      { key: 'right_profile', label: 'Right Profile', category: 'image' },
      { key: 'left_profile', label: 'Left Profile', category: 'image' },
    ],
  },
  {
    key: 'intraoral',
    label: 'Intraoral photos',
    hint: 'Occlusal and buccal views',
    slots: [
      { key: 'front', label: 'Front', category: 'image' },
      { key: 'right_buccal', label: 'Right Buccal', category: 'image' },
      { key: 'left_buccal', label: 'Left Buccal', category: 'image' },
      { key: 'upper_occlusal', label: 'Upper Occlusal', category: 'image' },
      { key: 'lower_occlusal', label: 'Lower Occlusal', category: 'image' },
    ],
  },
  {
    key: 'other',
    label: 'Other documents',
    hint: 'Prescriptions, treatment plans, anything else',
    slots: [
      { key: 'prescription', label: 'Prescription / Rx', category: 'doctor_instruction' },
      { key: 'treatment_plan', label: 'Treatment Plan', category: 'treatment_plan' },
      { key: 'other_document', label: 'Other Document', category: 'other' },
    ],
  },
];

export const WF_ARCHES = ['upper', 'lower', 'both'] as const;
export type WfArch = (typeof WF_ARCHES)[number];
export const WF_ARCH_LABELS: Record<WfArch, string> = { upper: 'Upper only', lower: 'Lower only', both: 'Both arches' };

/** What the clinic portal needs to render the wizard (server-supplied, never hardcoded client-side). */
export interface WfPortalMeta {
  doctorCode: string | null;
  doctorName: string | null;
  clinicName: string | null;
  caseTypes: Array<{ key: string; label: string }>;
  recordSlots: WfRecordSlotGroup[];
  arches: typeof WF_ARCHES;
  priorities: typeof WF_PRIORITIES;
}

export const wfPortalSubmitSchema = z.object({
  patientFirstName: z.string().trim().min(1, 'Patient first name is required').max(120),
  // Required: the surname is what a case is filed and searched under at the lab.
  patientLastName: z.string().trim().min(1, 'Patient last name is required').max(120),
  phone: optText(40),
  whatsapp: optText(40),
  email: optText(160),
  caseTypes: z.array(z.string().trim().min(1).max(64)).min(1, 'Choose at least one case type').max(10),
  arch: z.enum(WF_ARCHES).nullish(),
  newOrRefinement: z.enum(['new', 'refinement']).nullish(),
  priority: z.enum(WF_PRIORITIES).optional(),
  dueDate: optDate,
  iprStatus: z.enum(WF_REQ_STATUSES).optional(),
  etchingStatus: z.enum(WF_REQ_STATUSES).optional(),
  notes: optText(4000),
  patientInstructions: optText(4000),
});
export type WfPortalSubmit = z.infer<typeof wfPortalSubmitSchema>;

// ---------------------------------------------------------------------------------------------
// Dashboard / reports
// ---------------------------------------------------------------------------------------------

export interface WfDashboard {
  generatedAt: string;
  activeCases: number;
  byStage: Array<{ stageKey: string; name: string; group: WfStageGroup; count: number; overdue: number; stuck: number }>;
  overdue: number;
  dueToday: number;
  dueTomorrow: number;
  urgent: number;
  stuck: number;
  awaitingApproval: number;
  awaitingReview: number;
  onHold: number;
  inProduction: number;
  printing: number;
  printComplete: number;
  reprints: number;
  finishing: number;
  packaging: number;
  readyForDispatch: number;
  dispatchedToday: number;
  dispatchedThisMonth: number;
  /** Every case that reached dispatch (completed), all time. */
  dispatchedTotal: number;
  cancelled: number;
  newToday: number;
  myOpen: number;
  myDueToday: number;
  myOverdue: number;
  myCompletedToday: number;
}

export interface WfAgingRow {
  stageKey: string;
  name: string;
  slaHours: number | null;
  activeCases: number;
  avgHours: number;
  maxHours: number;
  stuck: number;
  completedVisits: number;
  avgHistoricalHours: number;
}
export interface WfAgingReport {
  generatedAt: string;
  rows: WfAgingRow[];
  bottleneckStage: string | null;
  stuckCases: Array<{ id: string; caseNumber: string; patientName: string; stageKey: string; hoursInStage: number; slaHours: number | null }>;
  workload: Array<{ userId: string | null; name: string; open: number; overdue: number; dueToday: number }>;
}

export interface WfSummaryReport {
  from: string;
  to: string;
  received: number;
  completed: number;
  dispatched: number;
  cancelled: number;
  onHold: number;
  reprints: number;
  reworks: number;
  byCaseType: Array<{ key: string; count: number }>;
  byDoctor: Array<{ key: string; count: number }>;
  byClinic: Array<{ key: string; count: number }>;
  byStaff: Array<{ key: string; count: number }>;
  byStage: Array<{ key: string; count: number }>;
  byReceivedThrough: Array<{ key: string; count: number }>;
  avgCompletionHours: number | null;
  avgApprovalHours: number | null;
  avgProductionHours: number | null;
  avgDispatchHours: number | null;
  overdue: number;
}

// ---------------------------------------------------------------------------------------------
// Notifications (in-app)
// ---------------------------------------------------------------------------------------------

export const WF_NOTIFICATION_TYPES = [
  'case_received',
  'case_assigned',
  'review_required',
  'approval_required',
  'case_rejected',
  'case_returned',
  'case_on_hold',
  'due_soon',
  'overdue',
  'printing_complete',
  'packaging_complete',
  'ready_for_dispatch',
  'dispatched',
  // Money: a clinic has declared a payment and someone must verify it.
  'payment_submitted',
  // Field: a rep's phone entered / left a known clinic's geofence (see field/service.ts).
  'rep_arrived',
  'rep_departed',
  // Field: a rep's phone is nearly flat, or its fixes look spoofed / impossible.
  'rep_battery_low',
  'rep_gps_suspect',
] as const;
export type WfNotificationType = (typeof WF_NOTIFICATION_TYPES)[number];

export interface WfNotification {
  id: string;
  userId: string | null;
  role: string | null;
  caseId: string | null;
  caseNumber: string | null;
  type: WfNotificationType;
  title: string;
  body: string | null;
  readAt: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------------------------
// Import / export
// ---------------------------------------------------------------------------------------------

export interface WfImportRowPreview {
  row: number;
  sheet: string;
  action: 'create' | 'update' | 'skip' | 'duplicate';
  reason: string | null;
  caseNumber: string | null;
  patientName: string;
  groupId: string | null;
  caseTypeRaw: string | null;
  caseType: string;
  statusRaw: string | null;
  stageKey: string;
  receivedAt: string | null;
}
export interface WfImportPreview {
  batchId: string;
  fileName: string;
  sheets: string[];
  totalRows: number;
  create: number;
  update: number;
  skip: number;
  duplicate: number;
  rows: WfImportRowPreview[];
  unknownStatuses: string[];
  unknownCaseTypes: string[];
}
export interface WfImportResult {
  batchId: string;
  created: number;
  updated: number;
  skipped: number;
  duplicates: number;
  errors: Array<{ row: number; message: string }>;
}
export interface WfImportBatch {
  id: string;
  fileName: string;
  importedAt: string;
  importedBy: string | null;
  rowsTotal: number;
  created: number;
  updated: number;
  skipped: number;
  duplicates: number;
  status: 'preview' | 'committed' | 'failed';
}

// ---------------------------------------------------------------------------------------------
// Default catalog (seeded on first boot; editable afterwards)
// ---------------------------------------------------------------------------------------------

/**
 * Semantic stage roles.
 *
 * Business rules — transition gates, notifications, report buckets, defaults — must refer to a ROLE
 * here, never to a raw stage key. Stages are lab-configurable and DO get renamed: the first rename
 * silently dead-lettered every hardcoded `to.key === '...'` check in the service, which fails open
 * (the gate simply never fires) rather than erroring, so nothing surfaces it. Renaming a stage now
 * means editing this map and nothing else.
 */
export const WF_STAGE_ROLES = {
  start: 'case_received',
  recordCheck: 'record_check',
  recordsDeficient: 'deficiency_in_records',
  intake: 'intake',
  planning: 'planning',
  design: 'cad_design',
  doctorApproval: 'for_dr_approval',
  invoice: 'invoice',
  printing: 'printing',
  punching: 'punching',
  cutting: 'cutting',
  packaging: 'packaging',
  dispatch: 'dispatched',
  completed: 'delivered_completed',
  onHold: 'on_hold',
  cancelled: 'case_cancelled',
} as const;

export const WF_DEFAULT_STAGES: WfStageInput[] = [
  // --- The onboarding pipeline ------------------------------------------------------------------
  // Case Received -> Record Check -> (Deficiency in Records | Scan) -> 3D Software -> 3D Planning ->
  // Dr Approval -> Invoice -> (On Hold | Printing) -> Punching -> Cutting -> Packaging -> Dispatched -> Delivered.
  // Keys are stable identifiers (code and old rows refer to them); only the names shown changed.
  // `allowedNext` encodes that routing, so the two decision points are enforced rather than trusted.
  { key: 'case_received', name: 'Case Received', sortOrder: 10, group: 'intake', color: '#0EA5E9', slaHours: 24, onBoard: true, allowedNext: ['record_check'] },
  // No leaving-checklist here on purpose: a checklist blocks EVERY exit except hold/cancel, which
  // would trap a case whose records are incomplete — the exact branch this stage exists to take.
  { key: 'record_check', name: 'Record Check', sortOrder: 20, group: 'records', color: '#6366F1', slaHours: 24, onBoard: true, allowedNext: ['deficiency_in_records', 'intake'] },
  { key: 'deficiency_in_records', name: 'Deficiency in Records', sortOrder: 30, group: 'records', color: '#F59E0B', slaHours: 72, onBoard: true, allowedNext: ['record_check', 'intake'] },
  { key: 'intake', name: 'Scan', sortOrder: 40, group: 'intake', color: '#0891B2', slaHours: 24, onBoard: true, allowedNext: ['planning'], checklist: ['Upper scan present and readable', 'Lower scan present and readable', 'Bite / occlusion recorded', 'Photographs complete', 'Prescription and instructions attached'] },
  { key: 'planning', name: '3D Software', sortOrder: 50, group: 'design', color: '#14B8A6', slaHours: 48, onBoard: true, allowedNext: ['cad_design'] },
  { key: 'cad_design', name: '3D Planning', sortOrder: 60, group: 'design', color: '#06B6D4', slaHours: 48, onBoard: true, allowedNext: ['for_dr_approval'] },
  // The back edge to CAD Design is what a rejection is: without it an unhappy doctor is a dead end.
  { key: 'for_dr_approval', name: 'Dr Approval', sortOrder: 70, group: 'approval', color: '#8B5CF6', slaHours: 120, onBoard: true, allowedNext: ['invoice', 'cad_design'] },
  { key: 'invoice', name: 'Invoice', sortOrder: 80, group: 'billing', color: '#EC4899', slaHours: 48, onBoard: true, allowedNext: ['printing', 'on_hold'] },
  { key: 'printing', name: 'Printing', sortOrder: 90, group: 'production', color: '#10B981', slaHours: 48, onBoard: true, allowedNext: ['punching'], requiredFields: ['productionInstructions'] },
  // Printed aligners are punched (attachment/IPR marks) and then trimmed; each stage counts its
  // upper and lower trays against the case's upper/lower aligner numbers (see WfCase.printedUpper…).
  { key: 'punching', name: 'Punching', sortOrder: 93, group: 'production', color: '#059669', slaHours: 24, onBoard: true, allowedNext: ['cutting'] },
  { key: 'cutting', name: 'Cutting', sortOrder: 96, group: 'production', color: '#047857', slaHours: 24, onBoard: true, allowedNext: ['packaging'] },
  { key: 'packaging', name: 'Packaging', sortOrder: 100, group: 'finishing', color: '#4D7C0F', slaHours: 24, onBoard: true, allowedNext: ['dispatched'], checklist: ['Product checked', 'Patient name checked', 'Group ID checked', 'Quantity checked', 'Upper/lower checked', 'Accessories included', 'Instructions included', 'Box packed', 'Final QC completed'] },
  // Dispatched is no longer the end of the line — delivery is.
  { key: 'dispatched', name: 'Dispatched', sortOrder: 110, group: 'dispatch', color: '#1D4ED8', slaHours: 72, onBoard: true, allowedNext: ['delivered_completed'] },
  { key: 'delivered_completed', name: 'Delivered / Completed', sortOrder: 120, group: 'terminal', color: '#22C55E', isTerminal: true, onBoard: false },

  // --- Cross-cutting states -----------------------------------------------------------------------
  // allowedNext is deliberately empty on hold: resume returns a case to wherever it was parked from.
  { key: 'on_hold', name: 'On Hold', sortOrder: 200, group: 'special', color: '#EAB308', slaHours: 168, onBoard: true },
  { key: 'case_cancelled', name: 'Case Cancelled', sortOrder: 210, group: 'terminal', color: '#DC2626', isTerminal: true, onBoard: false },

  // --- Product queues that run alongside the pipeline, not through it ------------------------------
  { key: 'retainers', name: 'Retainers', sortOrder: 300, group: 'special', color: '#0891B2', slaHours: 72, onBoard: true },
  { key: 'twin_block', name: 'Twin Block', sortOrder: 310, group: 'special', color: '#0E7490', slaHours: 120, onBoard: true },
  { key: 'zirconia_crowns', name: 'Zirconia Crowns', sortOrder: 320, group: 'special', color: '#155E75', slaHours: 120, onBoard: true },
  { key: 'appliances', name: 'Appliances', sortOrder: 330, group: 'special', color: '#164E63', slaHours: 120, onBoard: true },
];

export const WF_DEFAULT_OPTIONS: Array<Omit<WfOptionInput, 'sortOrder'>> = [
  // Case types (workbook terminology, spelling variants normalised by the importer)
  ...[
    ['aligners', 'Aligners'],
    ['clear_aligners', 'Clear Aligners'],
    ['zero_aligners', 'Zero Aligners'],
    ['refinement', 'Refinement'],
    ['retainers', 'Retainers'],
    ['night_guard', 'Night Guard'],
    ['initial_model', 'Initial Model'],
    ['initial_models', 'Initial Models'],
    ['appliance', 'Appliance'],
    ['twin_block', 'Twin Block'],
    ['space_maintainer', 'Space Maintainer'],
    ['space_maintainers', 'Space Maintainers'],
    ['expander', 'Expander'],
    ['zirconia', 'Zirconia'],
    ['zirconia_crown', 'Zirconia Crown'],
    ['crown', 'Crown'],
    ['pfm', 'PFM'],
    ['braces', 'Braces'],
    ['assessment', 'Assessment'],
    ['video_simulation', 'Video / Simulation'],
    ['only_upper', 'Only Upper'],
    ['only_lower', 'Only Lower'],
    ['four_model', '4 Model'],
    ['surgical_guide', 'Surgical Guide'],
    ['hawley', 'Hawley'],
    ['vfr', 'VFR'],
    ['cc_plate', 'CC Plate'],
    ['splint', 'Splint'],
    ['other', 'Other'],
  ].map(([key, label]) => ({ kind: 'case_type' as const, key: key!, label: label! })),
  ...[
    ['whatsapp', 'WhatsApp'],
    ['external_scan', 'External Scan'],
    ['in_house_scan', 'In House Scan'],
    ['model_impression', 'Model / Impression'],
    ['old_case', 'Old Case'],
    ['old_records', 'Old Records'],
    ['refinement', 'Refinement'],
    ['previous_records', 'Previous Records'],
    ['email', 'Email'],
    ['portal', 'Clinic Portal'],
    ['other', 'Other'],
  ].map(([key, label]) => ({ kind: 'received_through' as const, key: key!, label: label! })),
  ...[
    ['missing_scan', 'Missing scan'],
    ['bad_scan', 'Bad scan'],
    ['missing_records', 'Missing records'],
    ['doctor_clarification', 'Doctor clarification'],
    ['doctor_approval_pending', 'Doctor approval pending'],
    ['payment_pending', 'Payment pending'],
    ['production_issue', 'Production issue'],
    ['technical_issue', 'Technical issue'],
    ['approved_but_on_hold', 'Approved but on hold'],
    ['other', 'Other'],
  ].map(([key, label]) => ({ kind: 'hold_reason' as const, key: key!, label: label! })),
  ...[
    ['patient_declined', 'Patient declined'],
    ['doctor_cancelled', 'Doctor cancelled'],
    ['bad_scan', 'Bad scan / records unusable'],
    ['duplicate', 'Duplicate entry'],
    ['payment', 'Payment not received'],
    ['no_response', 'No response'],
    ['other', 'Other'],
  ].map(([key, label]) => ({ kind: 'cancel_reason' as const, key: key!, label: label! })),
  ...[
    ['whatsapp', 'WhatsApp'],
    ['phone', 'Phone call'],
    ['in_person', 'In person'],
    ['email', 'Email'],
    ['portal', 'Portal'],
    ['doctor', 'Via doctor'],
  ].map(([key, label]) => ({ kind: 'approval_method' as const, key: key!, label: label! })),
  ...[
    ['tcs', 'TCS'],
    ['leopards', 'Leopards'],
    ['mp', 'M&P'],
    ['rider', 'Lab rider'],
    ['hand_delivery', 'Hand delivery'],
    ['pickup', 'Clinic pickup'],
    ['other', 'Other'],
  ].map(([key, label]) => ({ kind: 'courier' as const, key: key!, label: label! })),
  // Punching sheet-stack codes (S = soft layer, H = hard layer). soft/hard ratios drive optional
  // sheet-consumption maths: sheets = (upperModels + lowerModels) x ratio. Admin-editable.
  ...[
    ['ssh', 'SSH', 1, 0.25],
    ['shh', 'SHH', 1, 1],
    ['ss', 'SS', 1, 0],
    ['hhh', 'HHH', 0, 1],
    ['hh', 'HH', 0, 1],
    ['sh', 'SH', 1, 1],
    ['s', 'S', 1, 0],
    ['1mm', '1mm', 0, 0],
    ['1_5mm', '1.5mm', 0, 0],
    ['soft_night_guard', 'Soft Night Guard', 0, 0],
    ['dont_punch', 'Dont Punch!!!', 0, 0],
  ].map(([key, label, soft, hard]) => ({
    kind: 'punching_type' as const,
    key: key as string,
    label: label as string,
    meta: { softRatio: soft as number, hardRatio: hard as number },
  })),
  ...WF_NOTIFICATION_TYPES.map((t) => ({
    kind: 'notification_rule' as const,
    key: t,
    label: t.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
    // Money notices go to whoever runs the ledger as well as management.
    meta: { enabled: true, roles: t === 'payment_submitted' ? ['manager', 'super_admin', 'accountant'] : ['manager', 'super_admin'] },
  })),
];

// The stored keys are unchanged (existing rows say 'emergency'), but what the tier MEANS to a
// clinic is a delivery promise — see shared/turnaround.ts for the days and the surcharge.
export const WF_PRIORITY_LABELS: Record<WfPriority, string> = {
  normal: 'Normal',
  // No longer offered — kept so a case booked under it still reads correctly.
  urgent: 'Urgent',
  emergency: 'Express',
};
export const WF_REQ_LABELS: Record<WfReqStatus, string> = {
  not_required: 'Not required',
  required: 'Required',
  pending: 'Pending',
  completed: 'Completed',
};
export const WF_APPROVAL_LABELS: Record<WfApprovalState, string> = {
  none: '-',
  simulation_ready: 'Simulation ready',
  requested: 'Approval requested',
  pending: 'Approval pending',
  approved: 'Approved',
  rejected: 'Rejected',
  changes_requested: 'Changes requested',
  approved_with_instructions: 'Approved with instructions',
};

/**
 * A patient, as this lab actually knows one: a person who appears on cases. There is a standalone
 * `patients` table in the older module, but nothing writes to it — the patient's name, phone and
 * doctor all live on the case, so the directory is derived from cases rather than kept in parallel.
 *
 * Identity is (normalised name + doctor group): the same name under two different doctors is two
 * different people, and the same person under one doctor is one entry however many cases they have.
 */
export interface WfPatientSummary {
  nameNorm: string;
  name: string;
  phone: string | null;
  doctorId: string | null;
  doctorName: string | null;
  clinicName: string | null;
  groupId: string | null;
  caseCount: number;
  lastReceivedAt: string;
}
